import { inject } from '@angular/core';

import { forkJoin, map, Observable, of } from 'rxjs';

import { tapResponse } from '@ngrx/operators';
import { patchState, StateSignals, WritableStateSource } from '@ngrx/signals';

import { TRANSLATION_ADAPTER } from '../adapters/translation-adapter.token';
import { TranslationDictionary } from '../models/translation-dictionary.type';
import { TranslationScope } from '../models/translation-scope.type';
import { TranslationState } from './translation.store';

export const translationAdapterMethodsFactory = (
  store: StateSignals<TranslationState> & WritableStateSource<TranslationState>,
  translationAdapter = inject(TRANSLATION_ADAPTER),
) => {
  const loadTranslations = (...scopes: TranslationScope[]): Observable<(Map<string, string> | void)[]> => {
    return forkJoin(scopes.map((scope) => {
      if (store._loadedScopes().has(scope)) {
        return of(void null);
      } else {
        return translationAdapter.loadTranslations(store.currentLanguage(), scope).pipe(
          map((dictionary) => transformDictionaryIntoMap(dictionary)),
          tapResponse({
            next: (translations) => patchState(store, ({ _translations, _loadedScopes }) => ({
              _loadedScopes: new Set([..._loadedScopes, scope]),
              _translations: new Map([..._translations, ...translations])
            })),
            error: (error) => { throw error },
          }),
        );
      }
    }));
  };

  const transformDictionaryIntoMap = (dictionary: TranslationDictionary, keyPrefix = ''): Map<string, string> => {
    return Object.entries(dictionary).reduce((map, [key, value]) => {
      const newKey = keyPrefix ? `${keyPrefix}.${key}` : key;

      if (typeof value === 'string') {
        map.set(newKey, value);
      } else {
        transformDictionaryIntoMap(value, newKey).forEach((val, key) => map.set(key, val));
      }

      return map;
    }, new Map<string, string>());
  };

  return { loadTranslations };
};
